import { useState, useEffect } from 'react';
import {
  DynamicFieldProperty_Enum,
  Template,
  TemplateType_Slug,
} from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { PrefillType } from '@weave/schema-gen-ts/dist/schemas/payments-platform/care-credit-integration/care_credit_integration.pb';
import type { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { ContactType_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { CareCreditIntegrationCustom } from '@frontend/api-care-credit-integration';
import { getUser } from '@frontend/auth-helpers';
import { theme } from '@frontend/theme';
import { formatCentsToCurrency } from '@frontend/currency';
import { useTranslation } from '@frontend/i18n';
import { ManualTemplateTypesWithStaticTemplate, SendInThreadModalContents } from '@frontend/integrated-messaging';
import {
  CareCreditMessageConsent,
  type CareCreditPatientData,
  type CareCreditPurlTransaction,
  getCareCreditTransactionDateRange,
  useCareCreditPurlQuery,
  type CareCreditDecoupledTransactionDraft,
  useCareCreditLocationStatus,
} from '@frontend/payments-care-credit';
import { genUUIDV4 } from '@frontend/string';
import {
  Button,
  DatePickerField,
  Modal,
  ModalProps,
  MoneyField,
  SpinningLoader,
  Text,
  TextField,
  useForm,
  useFormField,
} from '@frontend/design-system';

dayjs.extend(customParseFormat);

type CareCreditDecoupledPaymentLinkModalProps = Omit<ModalProps, 'children'> & {
  careCreditData: CareCreditPatientData;
  locationId: string;
  person?: Person;
  phoneNumber?: string;
  origin: 'inbox' | 'contact-panel';
  renderTemplate?: (args: { template: Template; additionalBindings?: Record<string, string> }) => void;
  getDefaultTemplate?: (args: { type: ManualTemplateTypesWithStaticTemplate }) => Template | undefined;
};

type ModalStep = 'consent' | 'transaction' | 'message';

export const CareCreditDecoupledPaymentLinkModal = ({
  careCreditData,
  locationId,
  person,
  phoneNumber: providedPhoneNumber,
  origin,
  renderTemplate,
  getDefaultTemplate,
  ...modalProps
}: CareCreditDecoupledPaymentLinkModalProps) => {
  const { t } = useTranslation('payments');
  const [step, setStep] = useState<ModalStep>('consent');
  const [transaction, setTransaction] = useState<CareCreditPurlTransaction>();
  const [invoiceId, setInvoiceId] = useState<string>();
  const [enablePurlFetch, setEnablePurlFetch] = useState(false);
  const { min: minimumServiceDate, max: maximumServiceDate } = getCareCreditTransactionDateRange();
  const minimumServiceDateDisplay = dayjs(minimumServiceDate).format('MM/DD/YYYY');
  const maximumServiceDateDisplay = dayjs(maximumServiceDate).format('MM/DD/YYYY');
  const {
    getFieldProps,
    isComplete: isTransactionComplete,
    reset: resetTransactionForm,
    validate: validateTransaction,
    values: transactionValues,
  } = useForm({
    fields: {
      amount: {
        type: 'money',
        required: true,
        validator: ({ value }) => {
          const amount = Number(String(value).replace(/[$,]/g, ''));
          if (!Number.isFinite(amount) || amount <= 0) return t('Enter an amount greater than zero');
          // sometimes careCreditData.amount gets set to zero somehow, but other values have value
          // add this back later. this happens when launching CC from the message tempalte modal
          // if (amount > careCreditData.amount) return t('Amount cannot exceed available CareCredit balance123');
          return '';
        },
      },
      invoiceNumber: {
        type: 'text',
      },
      serviceDate: {
        type: 'datePicker',
        required: true,
        value: dayjs().format('MM/DD/YYYY'),
        minDate: minimumServiceDateDisplay,
        maxDate: maximumServiceDateDisplay,
        validator: ({ value }) => {
          const date = dayjs(value, 'MM/DD/YYYY', true);
          if (!date.isValid() || date.isBefore(dayjs(minimumServiceDate)) || date.isAfter(dayjs(maximumServiceDate))) {
            return t('Service date must be within the last 120 days');
          }
          return '';
        },
      },
    },
  });
  const { showDecoupled } = useCareCreditLocationStatus(locationId);
  const userId = getUser()?.userID || '';
  const checkboxProps = useFormField({ type: 'checkbox', required: true, value: false });
  const {
    purl: purlQueryData,
    purlError,
    isPurlLoading,
    isPurlSuccess,
  } = useCareCreditPurlQuery(
    {
      locationId,
      person,
      isDecoupled: showDecoupled,
      userId,
      careCreditPurlTransaction: transaction,
      prefillType: PrefillType.PREFILL_TYPE_TRANSACTION_PROCESS,
    },
    { enabled: enablePurlFetch }
  );

  const patientFields = CareCreditIntegrationCustom.getPatientQueryFields(person);
  const phoneNumber =
    providedPhoneNumber ||
    person?.contactInfo?.find((contact) => contact.type === ContactType_Enum.PHONE_MOBILE)?.destination ||
    person?.contactInfo?.find((contact) => contact.type === ContactType_Enum.PHONE_HOME)?.destination ||
    patientFields?.contactNumber;

  const handleTransactionNext = () => {
    if (!isTransactionComplete) {
      validateTransaction();
      return;
    }

    const serviceDate = dayjs(String(transactionValues.serviceDate), 'MM/DD/YYYY', true);
    const draft: CareCreditDecoupledTransactionDraft = {
      amount: String(transactionValues.amount ?? '').replace(/[$,]/g, ''),
      invoiceNumber: String(transactionValues.invoiceNumber ?? ''),
      serviceDate: serviceDate.format('YYYY-MM-DD'),
    };

    setTransaction({
      purchaseAmountInCents: Math.round(Number(draft.amount) * 100),
      invoiceNumber: draft.invoiceNumber.trim(),
      serviceDate: draft.serviceDate,
      paymentRequestId: genUUIDV4(),
    });
    setInvoiceId(genUUIDV4());
    // in the case of contact panel, we wanna
    // setStep('message') here and let the modal continue
    // to display and transition itself to the message step
    //
    //in the case of inbox, we want to just
    // default back to the thread that's already open.
    if (origin === 'contact-panel') {
      setStep('message');
    }
    setEnablePurlFetch(true);
  };

  const handleClose = () => {
    setStep('consent');
    setTransaction(undefined);
    setInvoiceId(undefined);
    resetTransactionForm();
    modalProps.onClose();
  };

  useEffect(() => {
    if (isPurlSuccess && !!purlQueryData && !!purlQueryData.purl) {
      setEnablePurlFetch(false);
      if (origin === 'inbox' && renderTemplate && getDefaultTemplate) {
        // Render the template with the purl for inbox origin when template functions are provided
        const template = getDefaultTemplate({ type: TemplateType_Slug.MANUAL_CARE_CREDIT_COLLECT });
        if (template) {
          renderTemplate({
            template,
            additionalBindings: {
              [DynamicFieldProperty_Enum.PATIENT_CARE_CREDIT_URL]: purlQueryData.purl,
            },
          });
        }
        handleClose();
      }
    }
    // may need to add purl on Error handling here
  }, [isPurlSuccess, purlQueryData, origin, renderTemplate, getDefaultTemplate, handleClose]);

  return (
    <Modal.Responsive {...modalProps} onClose={handleClose} minWidth={600}>
      {step === 'consent' && (
        <>
          <Modal.Header onClose={handleClose}>{t('Text Message Consent')}</Modal.Header>
          <Modal.Body>
            <CareCreditMessageConsent checkboxProps={checkboxProps} isPayment phoneNumber={phoneNumber} />
          </Modal.Body>
          <Modal.Footer
            primary={{
              label: t('Next'),
              onClick: () => setStep('transaction'),
              disabled: !checkboxProps.value || !phoneNumber,
            }}
            secondary={{ label: t('Cancel'), onClick: handleClose }}
          />
        </>
      )}
      {step === 'transaction' && (
        <>
          <Modal.Header onClose={handleClose}>{t('Enter Transaction Info')}</Modal.Header>
          <Modal.Body>
            <div css={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Text>{t('Enter details for this CareCredit transaction.')}</Text>
              <MoneyField
                {...getFieldProps('amount')}
                name='amount'
                label={t('Transaction amount')}
                placeholder='$0.00'
                min={0}
                helperText={t('{{amount}} available', {
                  amount: formatCentsToCurrency(careCreditData.amount * 100),
                })}
              />
              <TextField
                {...getFieldProps('invoiceNumber')}
                name='invoiceNumber'
                aria-label={t('Invoice number')}
                label=''
                placeholder={t('Invoice number')}
                helperText={t('Use the invoice number from your PMS or billing system')}
              />
              <DatePickerField
                {...getFieldProps('serviceDate')}
                name='serviceDate'
                label={t('Service date')}
                minDate={minimumServiceDateDisplay}
                maxDate={maximumServiceDateDisplay}
              />
            </div>
          </Modal.Body>
          <Modal.Footer
            primary={{
              label: t('Next'),
              onClick: handleTransactionNext,
              disabled: !isTransactionComplete,
              loading: isPurlLoading,
            }}
            secondary={{ label: t('Cancel'), onClick: handleClose }}
            tertiary={{ label: t('Back'), iconName: 'arrow-left', onClick: () => setStep('consent') }}
          />
        </>
      )}
      {step === 'message' && (
        <>
          {isPurlLoading ? (
            <Modal.Body css={{ display: 'flex', minHeight: 240, alignItems: 'center', justifyContent: 'center' }}>
              <SpinningLoader size='small' />
            </Modal.Body>
          ) : purlError || !purlQueryData?.purl || !transaction || !invoiceId ? (
            <>
              <Modal.Header onClose={handleClose}>{t('Share in Message')}</Modal.Header>
              <Modal.Body>
                <Text color='error'>{t('Unable to create a CareCredit payment link. Please try again.')}</Text>
              </Modal.Body>
              <Modal.Footer>
                <Button variant='tertiary' onClick={() => setStep('transaction')}>
                  {t('Back')}
                </Button>
              </Modal.Footer>
            </>
          ) : (
            <div css={{ padding: theme.spacing(0, 3) }}>
              <SendInThreadModalContents
                groupId={locationId}
                onBack={() => setStep('transaction')}
                personId={person?.personId || ''}
                initPersonPhone={phoneNumber || ''}
                templateType={TemplateType_Slug.MANUAL_CARE_CREDIT_COLLECT}
                linkData={{ link: purlQueryData.purl, relatedId: invoiceId }}
                onSend={() => {
                  // is this going to break if the api call
                  // takes longer than 2s?
                  setTimeout(handleClose, 2000);
                }}
                onSchedule={handleClose}
                onClose={handleClose}
                contextPreviewProps={{
                  iconName: 'dollar-sign',
                  title: <Text weight='bold'>{formatCentsToCurrency(transaction.purchaseAmountInCents)}</Text>,
                  subtitle: <Text>{dayjs(transaction.serviceDate).format('MMM D, YYYY')}</Text>,
                }}
              />
            </div>
          )}
        </>
      )}
    </Modal.Responsive>
  );
};
